// React
import { Fragment, useMemo } from "react"

// Translations
import { useTrans } from "@/i18n"
import { useLang } from "@/context/useLang"

// Utils
import { getShareNumberRange } from "@/utils/helpers"

// GraphQL
import { keepPreviousData } from "@/lib/query"
import { useCurrentFinancialOverviewQuery, ProjectType } from "@/api/graphql"

// UI
import { CardBody } from "@/components/Card"
import {
	Table,
	TableBody,
	TableDataCell,
	TableHead,
	TableHeading,
	TableRowCell,
} from "@/components/table-controls/TableItems"
import {
	useReactTable,
	flexRender,
	getCoreRowModel,
	createColumnHelper,
	CellContext,
} from "@/lib/table"
import { Heading } from "@/components/Typography"
import { Tooltip } from "@/components/Tooltip"

// Utils
import { isWholeNumber } from "@/lib/math"

// Environment variables
import { EXTERNAL_PLATFORM_ZONHUB_INVESTMENT_CERTIFICATE } from "@/lib/env"

// Icons
import { FiDownload } from "@/lib/icons"

// Context
export {
	FinancialOverviewContext,
	type FinancialOverviewContextType,
} from "./FinancialOverviewContext"

const columnHelper = createColumnHelper<ProjectType>()

/**
 * FinancialOverviewTable
 * @returns
 */
export function FinancialOverviewTable() {
	// Translations
	const { formatCurrency } = useLang()
	const t = useTrans(["investments", "dashboard", "project"])

	const { data, isPlaceholderData, isLoading } =
		useCurrentFinancialOverviewQuery(undefined, {
			placeholderData: keepPreviousData,
		})

	const columns = useMemo(
		() => [
			columnHelper.accessor("name", {
				id: "name",
				header: () => (
					<TableHeading>
						{t("investments:fiscal.table_heading.project_name")}
					</TableHeading>
				),
			}),
			columnHelper.accessor(
				(data: ProjectType) =>
					data?.investor_shares_value_stats?.total_shares
						? isWholeNumber(
								data.investor_shares_value_stats?.total_shares,
							)
							? data.investor_shares_value_stats?.total_shares
							: Number(
									data.investor_shares_value_stats
										?.total_shares ?? 0,
								).toFixed(2)
						: "-",
				{
					id: "total_shares",
					header: () => (
						<TableHeading>
							{t(
								"dashboard:financial_overview_table.heading.total_shares",
							)}
						</TableHeading>
					),
				},
			),
			columnHelper.accessor(
				(data: ProjectType) =>
					formatCurrency(
						Number(
							data.investor_shares_value_stats
								?.total_nominal_investment,
						) ?? 0,
					),
				{
					id: "original_value",
					header: () => (
						<TableHeading>
							{t(
								"dashboard:financial_overview_table.heading.original_value",
							)}
						</TableHeading>
					),
				},
			),
			columnHelper.accessor(
				(data: ProjectType) =>
					data.investor_shares_value_stats?.total_investment_value
						? formatCurrency(
								parseFloat(
									data.investor_shares_value_stats
										?.total_investment_value,
								) ?? 0,
							)
						: "-",
				{
					id: "total_investment_value",
					header: () => (
						<TableHeading>
							{t("project:dashboard.hero.share_value.title")}
						</TableHeading>
					),
				},
			),
			columnHelper.accessor((data) => data, {
				id: "share_numbers",
				header: () => (
					<TableHeading>
						{t(
							"investments:fiscal.table_heading.share_numbers_at_end_of_year",
						)}
					</TableHeading>
				),
				cell: (info: CellContext<ProjectType, ProjectType>) => (
					<Tooltip
						content={`Download certificaat voor obligatienummers: ${
							getShareNumberRange(
								info
									.getValue()
									?.investor_shares_value_stats?.shares?.map(
										(share) => Number(share?.share_number),
									) ?? [],
								t,
							) ?? undefined
						}`}
					>
						<a
							href={`${EXTERNAL_PLATFORM_ZONHUB_INVESTMENT_CERTIFICATE}${
								info.getValue()?.id
							}`}
							className="flex text-sm underline hover:text-gray-600"
						>
							<FiDownload className="mr-2" />
							<span className="flex-1">
								{getShareNumberRange(
									info
										.getValue()
										?.investor_shares_value_stats?.shares?.map(
											(share) =>
												Number(share?.share_number),
										) ?? [],
									t,
								) ?? undefined}
							</span>
						</a>
					</Tooltip>
				),
			}),
		],
		[t, formatCurrency],
	)
	const tableData = useMemo(
		() => (data?.me?.investment_projects?.results as ProjectType[]) ?? [],
		[data?.me?.investment_projects?.results],
	)
	const table = useReactTable({
		columns,
		data: tableData,
		getCoreRowModel: getCoreRowModel(),
	})

	// Data state
	return (
		<>
			<Table
				className="min-w-[48rem] lg:min-w-0"
				isLoading={isLoading}
				headerGroups={table.getHeaderGroups()}
			>
				{/* table header */}
				<TableHead>
					{table.getHeaderGroups().map((headerGroup) => (
						<TableRowCell key={headerGroup.id}>
							{headerGroup.headers.map((header) => {
								return (
									<Fragment key={header.id}>
										{flexRender(
											header.column.columnDef.header,
											header.getContext(),
										)}
									</Fragment>
								)
							})}
						</TableRowCell>
					))}
				</TableHead>
				{/* table body and table cells */}
				<TableBody
					className={isPlaceholderData ? "opacity-25" : ""}
					data-testid="tablebody"
				>
					{table.getRowModel().rows.map((row, index) => {
						return (
							<TableRowCell
								key={row.id}
								isOdd={index % 2 === 0}
								data-testid={`tablerow-${row.id}`}
							>
								{row.getAllCells().map((cell) => {
									return (
										<TableDataCell
											key={cell.id}
											className="break-word whitespace-pre-wrap"
										>
											{flexRender(
												cell.column.columnDef.cell,
												cell.getContext(),
											)}
										</TableDataCell>
									)
								})}
							</TableRowCell>
						)
					})}
				</TableBody>
			</Table>
			{data?.me?.investment_projects?.results?.length === 0 && (
				<CardBody>
					<TableEmptyState />
				</CardBody>
			)}
		</>
	)
}

const TableEmptyState = () => {
	const t = useTrans("investments")

	return (
		<div className="space-y-4 p-10 text-center">
			<Heading as="h2" styleAs="h5">
				{t("investments:fiscal.no_results.title")}
			</Heading>
			<p className="text-gray-600">
				{t("investments:fiscal.no_results.copy")}
			</p>
		</div>
	)
}
